import { useState, useEffect, useRef, forwardRef } from "react"
import { Pledge, GivingPerson } from "../types"
import { css } from "@emotion/react"
import { Heading } from "@planningcenter/doxy-web"
import { formattedCents } from "source/giving/utils/money"
import { colors } from "source/shared/colors"
import {
  GivingPersonProvider,
  useGivingPersonContext,
} from "../hooks/useGivingPersonContext"

export default function PledgeProgressBar({
  jointGiver,
  pledge,
  isConcluded,
}: {
  jointGiver: GivingPerson | null
  pledge: Pledge
  isConcluded: boolean
}) {
  return (
    <GivingPersonProvider>
      <PledgeProgressBarContent
        jointGiver={jointGiver}
        pledge={pledge}
        isConcluded={isConcluded}
      />
    </GivingPersonProvider>
  )
}

function PledgeProgressBarContent({
  jointGiver,
  pledge,
  isConcluded,
}: {
  jointGiver: GivingPerson | null
  pledge: Pledge
  isConcluded: boolean
}) {
  const progressBarRef = useRef<HTMLDivElement>(null)
  const progressLabelRef = useRef<HTMLSpanElement>(null)

  const [progressLabelPosition, setprogressLabelPosition] = useState<
    "inside" | "outside"
  >("inside")

  // this allows for 8px of padding on each side of the inline label (plus 1px buffer)
  const progressLabelPadding = 17

  useEffect(() => {
    setTimeout(() => {
      const progressWidth = progressBarRef.current?.clientWidth ?? 0
      const inlineWidth = progressLabelRef.current?.clientWidth ?? 0

      if (progressWidth < inlineWidth + progressLabelPadding) {
        setprogressLabelPosition("outside")
      } else {
        setprogressLabelPosition("inside")
      }
    }, 500)
  }, [pledge.joint_progress_percent])

  return (
    <div className="d-f fd-c g-1">
      <div className="fs-13 fw-500 c-tint2">My progress</div>
      <ProgressBarContainer>
        <ProgressBar
          ref={progressBarRef}
          pledge={pledge}
          isConcluded={isConcluded}
        >
          <ProgressLabel
            ref={progressLabelRef}
            pledge={pledge}
            isConcluded={isConcluded}
            position={progressLabelPosition}
          />
        </ProgressBar>
      </ProgressBarContainer>
      <ProgressDescription jointGiver={jointGiver} pledge={pledge} />
    </div>
  )
}

function ProgressBarContainer({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="d-b p-r o-h w-100%"
      css={css`
        background-color: var(--color-tint6);
        height: 20px;
        border-radius: 40px;
      `}
    >
      {children}
    </div>
  )
}

const ProgressBar = forwardRef(function ProgressBar(
  {
    pledge,
    isConcluded,
    children,
  }: {
    pledge: Pledge
    isConcluded: boolean
    children: React.ReactNode
  },
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const progressWidth = pledge.joint_progress_percent
    ? `${pledge.joint_progress_percent}`
    : "0%"
  const backgroundImage = isConcluded
    ? "gray"
    : `linear-gradient(
        135deg,
        ${colors.brandGradientStart} 0%,
        ${colors.brandGradientEnd} 100%
      )`

  const backgroundColor = isConcluded ? "var(--color-tint4)" : "transparent"

  return (
    <div
      ref={ref}
      className="d-ib p-a h-100%"
      css={css`
        left: 0;
        top: 0;
        width: ${progressWidth};
        max-width: 100%;
        border-radius: 40px;
        background-image: ${backgroundImage};
        background-color: ${backgroundColor};
        user-select: none;
        transition: 500ms ease all;
      `}
    >
      {children}
    </div>
  )
})

const ProgressLabel = forwardRef(function ProgressLabel(
  {
    pledge,
    isConcluded,
    position,
  }: {
    pledge: Pledge
    isConcluded: boolean
    position: "inside" | "outside"
  },
  ref: React.ForwardedRef<HTMLSpanElement>,
) {
  const color =
    isConcluded || position === "outside" ? "inherit" : "var(--color-tint10)"
  const rightValue = position === "inside" ? "8px" : "auto"
  const leftValue = position === "inside" ? "auto" : "calc(100% + 8px)"

  return (
    <span
      ref={ref}
      className="d-f ai-c p-a w-a h-100% fw-500 ta-r"
      css={css`
        top: 0;
        right: ${rightValue};
        left: ${leftValue};
        color: ${color};
        font-size: 12px;
        transition: 500ms ease all;
      `}
    >
      {pledge.joint_progress_percent}
    </span>
  )
})

function ProgressDescription({
  jointGiver,
  pledge,
}: {
  jointGiver: GivingPerson | null
  pledge: Pledge
}) {
  const { givingPerson } = useGivingPersonContext()
  return (
    <>
      <div className="d-f jc-sb ai-c">
        <div className="d-f g-4p">
          <Heading
            level={4}
            size={5}
            text={`${formattedCents(pledge.joint_donated_total_cents, {
              noCentsIfWhole: true,
            })}`}
            color="tint1"
          />{" "}
          <span className="fs-13 fw-500 c-tint2">donated</span>
        </div>
        <div className="d-f g-4p">
          <Heading
            level={4}
            size={5}
            text={`${formattedCents(pledge.joint_amount_cents, {
              noCentsIfWhole: true,
            })}`}
            color="tint1"
          />
          {""}
          <span className="fs-13 fw-500 c-tint2">pledged</span>
        </div>
      </div>
      {jointGiver && pledge.joint_giver_amount_cents && (
        <div className="fs-5 c-tint2">
          (
          {formattedCents(pledge.amount_cents, {
            noCentsIfWhole: true,
          })}{" "}
          by {givingPerson.first_name} {givingPerson.last_name} /{" "}
          {formattedCents(pledge.joint_giver_amount_cents, {
            noCentsIfWhole: true,
          })}{" "}
          by {jointGiver.first_name} {jointGiver.last_name})
        </div>
      )}
    </>
  )
}
